// As partes do alvo que NÃO seguem a regra em edição (design D13, `overrides` da prévia de
// impacto): um insumo, grupo Ampli ou grupo do ERP com regra própria dentro do serviço,
// subetapa ou etapa. O motor dá precedência à regra mais específica, então essas partes
// ficam com a forma delas — e o cliente precisa vê-las para analisar e corrigir, não
// descobrir depois na curva.
//
// "Editar regra" abre o editor da regra que governa a parte. Com `onDecide` (D19), o bloco
// também pede a decisão antes de salvar: manter as regras dessas partes (a regra do alvo
// distribui só o que sobra) ou aplicar a regra do alvo a todo o valor (as regras listadas
// são desativadas ao salvar).
import { describeProfile } from "./profile";
import { TargetValue } from "./RuleTargetPicker";

const kindLabel = (target) => {
  const key = String(target?.key ?? "");
  if (target?.kind === "resource") return "Insumo";
  if (key.startsWith("resource_group:ampli:")) return "Grupo Ampli";
  if (key.startsWith("resource_group:")) return "Grupo do ERP";
  if (target?.kind === "node") return "EAP";
  return "Regra";
};

const profileText = (profile) => {
  try {
    return describeProfile(profile);
  } catch {
    return "Forma indisponível";
  }
};

const OverrideRow = ({ override, onEdit }) => (
  <li className="ampli-rule-overrides__row">
    <div className="ampli-rule-overrides__main">
      <span className="ampli-rule-overrides__kind">
        {kindLabel(override.target)}
      </span>
      <strong>{override.target?.label ?? override.ruleKey}</strong>
      <span className="ampli-rule-overrides__detail">
        {`${profileText(override.profile)} · ${override.serviceCount} ${
          override.serviceCount === 1 ? "serviço" : "serviços"
        }`}
      </span>
    </div>
    <span className="ampli-rule-overrides__value">
      <TargetValue cents={override.redistributedCents} />
    </span>
    {onEdit && override.ruleId ? (
      <button
        type="button"
        className="button button-outline small"
        aria-label={`Editar regra de ${
          override.target?.label ?? override.ruleKey
        }`}
        onClick={() => onEdit(override)}
      >
        Editar regra
      </button>
    ) : null}
  </li>
);

export const OVERRIDE_DECISIONS = Object.freeze({
  keep: "keep",
  override: "override",
});

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// A escolha entre manter as regras das partes e aplicar a regra do alvo a todo o valor.
const OverrideDecision = ({
  overrides,
  decision,
  onDecide,
  keepCents,
  targetCents,
  canOverride,
}) => {
  const outside = overrides.filter(
    (override) => override.outsideServiceCount > 0,
  );
  return (
    <>
      <fieldset className="ampli-rule-overrides__decision">
        <legend>Como tratar estas regras?</legend>
        <label className="ampli-rule-overrides__choice">
          <input
            type="radio"
            name="ampli-rule-override-decision"
            value={OVERRIDE_DECISIONS.keep}
            checked={decision === OVERRIDE_DECISIONS.keep}
            onChange={() => onDecide(OVERRIDE_DECISIONS.keep)}
          />
          <span className="ampli-rule-overrides__choice-text">
            <strong>Manter as regras próprias</strong>
            <span>
              {"Esta regra distribui "}
              <TargetValue cents={keepCents} />
              {", o valor do alvo sem essas partes. Elas seguem a forma delas."}
            </span>
          </span>
        </label>
        <label className="ampli-rule-overrides__choice">
          <input
            type="radio"
            name="ampli-rule-override-decision"
            value={OVERRIDE_DECISIONS.override}
            checked={decision === OVERRIDE_DECISIONS.override}
            disabled={!canOverride}
            onChange={() => onDecide(OVERRIDE_DECISIONS.override)}
          />
          <span className="ampli-rule-overrides__choice-text">
            <strong>Aplicar esta regra a todo o valor</strong>
            <span>
              {`${
                overrides.length === 1
                  ? "A regra acima é desativada"
                  : `As ${overrides.length} regras acima são desativadas`
              } ao salvar (ficam no histórico e podem ser reativadas), e `}
              <TargetValue cents={targetCents} />
              {" seguem esta forma."}
            </span>
            {outside.map((override) => (
              <span
                key={override.ruleKey}
                className="ampli-rule-overrides__warning"
                data-testid="rule-override-outside"
              >
                {`${
                  override.target?.label ?? override.ruleKey
                } também vale em ${plural(
                  override.outsideServiceCount,
                  "serviço",
                  "serviços",
                )} fora deste alvo, que deixam de seguir a regra.`}
              </span>
            ))}
            {!canOverride ? (
              <span className="ampli-rule-overrides__warning">
                Descarte a proposta de IA para aplicar esta regra a todo o
                valor.
              </span>
            ) : null}
          </span>
        </label>
      </fieldset>
      {decision !== OVERRIDE_DECISIONS.override && keepCents <= 0 ? (
        <p
          className="ampli-rule-overrides__notice"
          role="status"
          data-testid="rule-overrides-nothing-left"
        >
          As regras próprias cobrem todo o valor deste alvo: mantendo-as, esta
          regra não move nada.
        </p>
      ) : null}
    </>
  );
};

// Um estado por vez: carregando, falha, nenhuma exceção ou a lista.
const OverridesBody = ({ status, overrides, onEdit, decisionProps }) => {
  if (status === "loading") {
    return (
      <p className="card-label" role="status">
        Verificando insumos com regra diferente…
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="card-label" role="status">
        Não foi possível verificar os insumos com regra própria agora.
      </p>
    );
  }
  if (overrides.length === 0) {
    return (
      <p className="card-label" data-testid="rule-overrides-empty">
        Nenhum insumo deste alvo tem regra diferente: todo o valor segue esta
        regra.
      </p>
    );
  }
  return (
    <>
      {decisionProps ? (
        <p className="ampli-rule-overrides__lead">
          {"Estas partes têm regra própria, mais específica, com outra forma. "}
          <strong>Escolha abaixo</strong>
          {" se elas mantêm a forma delas ou passam a seguir esta regra."}
        </p>
      ) : (
        <p className="ampli-rule-overrides__lead">
          {
            "Estas partes seguem a regra própria delas, que é mais específica, e "
          }
          <strong>não mudam com esta regra</strong>
          {
            ". Confira se a forma de cada uma faz sentido e corrija se precisar."
          }
        </p>
      )}
      <ul>
        {overrides.map((override) => (
          <OverrideRow
            key={override.ruleKey}
            override={override}
            onEdit={onEdit}
          />
        ))}
      </ul>
      {decisionProps ? (
        <OverrideDecision overrides={overrides} {...decisionProps} />
      ) : null}
    </>
  );
};

const RuleOverridesSection = ({
  impact,
  onEditOverride,
  // D19: a decisão é pedida quando o editor passa `onDecide`.
  decision = null,
  onDecide,
  keepCents = 0,
  targetCents = 0,
  canOverride = true,
}) => {
  if (!impact || impact.status === "idle" || impact.status === "unavailable")
    return null;
  const overrides = impact.answer?.overrides ?? [];
  return (
    <section
      className="ampli-rule-overrides"
      aria-labelledby="ampli-rule-overrides-title"
    >
      <h3 id="ampli-rule-overrides-title">
        Insumos com regra própria neste alvo
      </h3>
      <OverridesBody
        status={impact.status}
        overrides={overrides}
        onEdit={onEditOverride}
        decisionProps={
          onDecide
            ? { decision, onDecide, keepCents, targetCents, canOverride }
            : null
        }
      />
    </section>
  );
};

export default RuleOverridesSection;
